/**
 * API publique de l'analytique produit.
 *
 * Les composants n'importent que `track` : la file, les lots, l'identité et
 * l'opposition sont l'affaire de `client.ts`. Un seul point d'entrée signifie
 * un seul endroit à modifier le jour où la destination change.
 *
 * ```ts
 * import { track } from "@/lib/analytics";
 *
 * track({ name: "piece_opened", properties: { source: "catalog", pieceId } });
 * ```
 */
export {
  setAppVersion,
  getAppVersion,
  setOptedOut,
  isOptedOut,
  flush,
  startAnalytics,
  resetAnalyticsSession,
  onAnalyticsSessionReset,
  newUuidV7,
  OPT_OUT_STORAGE_KEY,
} from "./client";
export type {
  VirtuosaEvent,
  VirtuosaEventName,
  PieceSource,
  PieceLoadFailureReason,
  UploadKind,
  UploadFailureReason,
  ExerciseStartFailureReason,
  NavigationType,
  JourneyKind,
  StudioContextType,
  StudioEntryDoor,
  StudioEmptyOrigin,
  StudioEmptyDismissReason,
} from "./events";

import { enqueue, onAnalyticsSessionReset } from "./client";
import type { AuthTrigger, PieceSource, VirtuosaEvent } from "./events";

/**
 * Les déclencheurs dont l'offre est **annoncée par une surface à part**, avant
 * que la modale n'existe.
 *
 * Pour tous les autres — le header, « Ajouter à mes morceaux », l'onglet
 * « Mes morceaux » — la modale *est* l'offre : son ouverture et son exposition
 * sont le même instant, et il n'y a rien entre les deux à mesurer. La capsule
 * de fin de phrase, elle, propose d'abord et n'ouvre la modale que si on
 * accepte : compter son ouverture comme une exposition de plus gonflerait le
 * dénominateur de l'entonnoir d'exactement le nombre d'acceptations, et le
 * taux de clic se lirait 30/130 là où il vaut 30/100.
 *
 * D'où `auth_prompt_opened`, qui n'est émis que pour ces déclencheurs-là.
 */
const ANNOUNCED_BEFORE_MODAL: ReadonlySet<AuthTrigger> = new Set<AuthTrigger>([
  "phrase_validated",
]);

/** Le nom que l'écran de compte doit émettre à son ouverture. */
export function authOpeningEventName(
  trigger: AuthTrigger,
): "auth_prompt_shown" | "auth_prompt_opened" {
  return ANNOUNCED_BEFORE_MODAL.has(trigger) ? "auth_prompt_opened" : "auth_prompt_shown";
}

/** Met un événement en file. Ne lève jamais, ne bloque jamais. */
export function track(event: VirtuosaEvent): void {
  enqueue(event);
}

/**
 * La provenance annoncée pour la prochaine ouverture d'un morceau précis.
 *
 * # Le problème qu'elle résout
 *
 * `piece_opened` décrit désormais **le chargement** dans le studio, pas le clic
 * qui le déclenche : c'est le seul instant comparable d'une provenance à
 * l'autre. Mais le studio ne peut deviner la provenance que par la route d'où
 * part le geste, et une route ne dit pas tout — le morceau de récompense d'un
 * parcours s'ouvre depuis sa fiche de catalogue, exactement comme n'importe
 * quel morceau de catalogue. Sans cette passation, `source: "program"` n'aurait
 * plus jamais d'émetteur.
 *
 * La surface qui *sait* d'où vient le geste l'annonce donc, et le chargement la
 * consomme. Un seul événement, à l'instant juste, avec la bonne provenance.
 *
 * # Pourquoi c'est sûr
 *
 * L'annonce est nominative : elle ne vaut que pour le morceau nommé, et elle se
 * consomme en une fois. Cliquer une récompense puis ouvrir autre chose ne
 * transporte donc rien, et l'annonce oubliée expire d'elle-même au chargement
 * suivant du même morceau, où elle est de toute façon juste.
 */
let announcedPieceSource: { pieceId: string; source: PieceSource } | null = null;

/** Annonce la provenance de l'ouverture à venir, depuis la surface qui la connaît. */
export function announcePieceSource(pieceId: string, source: PieceSource): void {
  announcedPieceSource = { pieceId, source };
}

/** Reprend la provenance annoncée pour ce morceau, et l'oublie. */
export function consumePieceSource(pieceId: string): PieceSource | null {
  if (announcedPieceSource === null || announcedPieceSource.pieceId !== pieceId) {
    return null;
  }

  const { source } = announcedPieceSource;

  announcedPieceSource = null;

  return source;
}

/**
 * Clés déjà émises pour cette session, pour les jalons d'activation qu'un
 * remontage de composant ne doit pas compter deux fois (un `useEffect` qui
 * rejoue ferait apparaître deux ouvertures de morceau pour une seule).
 *
 * En mémoire : le dédoublonnage vaut pour la session courante, pas au-delà.
 */
const emittedOnce = new Set<string>();

/**
 * Émet au plus une fois par session pour une clé donnée.
 *
 * @param key Identité logique du jalon, p.ex. `piece_opened:${pieceId}`.
 */
export function trackOnce(key: string, event: VirtuosaEvent): void {
  if (emittedOnce.has(key)) {
    return;
  }

  emittedOnce.add(key);
  enqueue(event);
}

/*
  Une nouvelle session anonyme oublie ce qui valait pour la précédente.

  Sans ça, le contrat écrit juste au-dessus — « pour la session courante, pas
  au-delà » — était faux : sur un poste partagé, la personne qui se connecte
  après une déconnexion héritait des jalons déjà émis par la précédente, et son
  branchement de clavier, son ouverture de morceau ou sa provenance annoncée
  disparaissaient sans laisser de trace.
*/
onAnalyticsSessionReset(() => {
  emittedOnce.clear();
  announcedPieceSource = null;
});
